import React, {ReactNode, useEffect, useRef} from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {useLocation} from 'react-router-dom';

interface ScrollbarProps {
  children: ReactNode;
  className?: string;
  scrollToTop?: boolean;

  [x: string]: any;
}

// Le handler molette de perfect-scrollbar (1.5.0 comme 1.5.6) est défectueux dès qu'un scroll natif
// (overflow:auto) est imbriqué dans un conteneur PS, ce qui est le cas de nos pages « apps » :
//  - son test « l'enfant peut encore scroller » a les directions inversées : sur un tableau scrollé
//    au milieu, la molette est donc ignorée par PS alors qu'elle devrait enchaîner sur la page, et
//    inversement ;
//  - son test « aux bornes » utilise une égalité stricte (scrollTop + offsetHeight === scrollHeight)
//    qui est fausse avec des hauteurs fractionnaires : PS fait alors stopPropagation + preventDefault
//    à chaque cran et plus rien (page ni tableau) ne scrolle.
// On retire donc le handler 'wheel' de PS (rails, drag, clavier, tactile et synchro restent gérés
// par PS) et on le remplace par useWheelScroll ci-dessous.
const PS_OPTIONS = {
  handlers: ['click-rail', 'drag-thumb', 'keyboard', 'touch'],
};

const WHEEL_PIXELS_PER_LINE = 20;
const EDGE_TOLERANCE = 1;

type Axis = 'x' | 'y';

const axisMetrics = (el: HTMLElement, axis: Axis) =>
  axis === 'y'
    ? {pos: el.scrollTop, view: el.clientHeight, content: el.scrollHeight}
    : {pos: el.scrollLeft, view: el.clientWidth, content: el.scrollWidth};

// Peut-on encore scroller `el` dans le sens de delta (delta > 0 : vers le bas / la droite) ?
const canScrollFurther = (el: HTMLElement, axis: Axis, delta: number) => {
  const {pos, view, content} = axisMetrics(el, axis);
  if (content - view < EDGE_TOLERANCE) return false;
  return delta > 0 ? pos + view < content - EDGE_TOLERANCE : pos > 0;
};

// Un élément entre la cible et le conteneur qui scrolle nativement dans ce sens doit garder la molette.
const isConsumedByNativeChild = (
  target: EventTarget | null,
  container: HTMLElement,
  axis: Axis,
  delta: number,
) => {
  let cursor = target instanceof Element ? target : null;
  while (cursor && cursor !== container) {
    if (cursor instanceof HTMLElement) {
      if (cursor.classList.contains('ps__child--consume')) return true;
      const style = window.getComputedStyle(cursor);
      const overflow = axis === 'y' ? style.overflowY : style.overflowX;
      if (/(auto|scroll)/.test(overflow) && canScrollFurther(cursor, axis, delta)) {
        return true;
      }
    }
    cursor = cursor.parentElement;
  }
  return false;
};

const wheelDeltaInPixels = (e: WheelEvent) => {
  const factor = e.deltaMode === 1 ? WHEEL_PIXELS_PER_LINE : 1;
  const dx = e.deltaX * factor;
  const dy = e.deltaY * factor;
  return e.shiftKey ? {dx: dy, dy: dx} : {dx, dy};
};

// Mêmes critères que perfect-scrollbar pour décider si le conteneur est scrollable (cf. update-geometry),
// afin de ne jamais consommer une molette que PS annulerait aussitôt en remettant scrollTop à 0.
const isScrollable = (el: HTMLElement, axis: Axis) => {
  const rect = el.getBoundingClientRect();
  const view = Math.ceil(axis === 'y' ? rect.height : rect.width);
  const content = axis === 'y' ? el.scrollHeight : el.scrollWidth;
  return view < content;
};

const useWheelScroll = (containerRef: React.MutableRefObject<HTMLElement | null>) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      if (e.defaultPrevented || e.ctrlKey) return; // ctrl + molette = zoom navigateur

      const {dx, dy} = wheelDeltaInPixels(e);
      const axis: Axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
      const delta = axis === 'y' ? dy : dx;
      if (!delta) return;

      if (isConsumedByNativeChild(e.target, container, axis, delta)) return;
      if (!isScrollable(container, axis) || !canScrollFurther(container, axis, delta)) {
        return; // aux bornes : on laisse la molette remonter au conteneur parent
      }

      if (axis === 'y') container.scrollTop += delta;
      else container.scrollLeft += delta;
      e.preventDefault();
      e.stopPropagation();
    };

    container.addEventListener('wheel', onWheel, {passive: false});
    return () => container.removeEventListener('wheel', onWheel);
  }, [containerRef]);
};

const Scrollbar: React.FC<ScrollbarProps> = React.forwardRef((props, ref) => {
  const {children, scrollToTop = true, className = '', ...others} = props;
  let _scrollBarRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const {pathname} = useLocation();

  useWheelScroll(containerRef);

  useEffect(() => {
    if (scrollToTop && _scrollBarRef) {
      // @ts-ignore
      _scrollBarRef._container.scrollTop = 0;
    }
  }, [_scrollBarRef, pathname, scrollToTop]);

  return (
    <PerfectScrollbar
      ref={ref => {
        // @ts-ignore
        _scrollBarRef = ref;
      }}
      options={PS_OPTIONS}
      containerRef={(el: HTMLElement | null) => {
        containerRef.current = el;
      }}
      {...others}
      className={className}>
      {children}
    </PerfectScrollbar>
  );
});

export default Scrollbar;
